import { IconsDirective } from '@awork/_shared/directives/icons/icons.directive'
import type { ActionButtonVariant } from '../../models/action-button-variant'
import { Component, computed, input } from '@angular/core'

export type IconButtonSelectedTone = 'favorite' | 'priority' | 'pin'

@Component({
  selector: 'aw-icon-button',
  templateUrl: './icon-button.component.html',
  styleUrls: ['./icon-button.component.scss'],
  imports: [IconsDirective]
})
export class IconButtonComponent {
  readonly variant = input<ActionButtonVariant>()
  readonly designVariant = computed(() => this.getDesignVariant())
  /** Custom icon. Omit it to use the automatic favourite stars. */
  readonly icon = input<string>()
  readonly style = input<'outline' | 'outlined' | 'clear' | 'filled' | 'subtle' | 'plain'>('clear')
  readonly pressed = input<boolean>()
  readonly selected = input(false)
  /** Applies the selected colour. Favourite also supplies a star when no icon is given. */
  readonly selectedTone = input<IconButtonSelectedTone>()
  readonly displayIcon = computed(() => this.getDisplayIcon())
  readonly color = input<'steel' | 'red' | 'blue' | 'white' | 'green'>('steel')
  readonly size = input<'l' | 'm' | 's'>('m')
  readonly iconSize = input<'xxl' | 'xl' | 'l' | 'm' | 's'>('m')
  /** Expands the button to the width of its container. */
  readonly block = input(false)
  readonly disabled = input(false)
  readonly loading = input(false)
  readonly iconColor = input<string>()
  /** Optional CSS color for an icon that uses a semantic theme token. */
  readonly iconColorValue = input<string>()
  readonly ariaLabel = input<string>()
  /** Marks the button as a disclosure control for collapsible content. */
  readonly ariaExpanded = input<boolean>()
  /** Id of the content that the disclosure button shows or hides. */
  readonly ariaControls = input<string>()
  /**
   * Uses the web favourite icons for both favourite states.
   * @withSignal {InputSignal<IconButtonSelectedTone | undefined>} selectedTone
   * @withSignal {InputSignal<boolean>} selected
   * @withSignal {InputSignal<string | undefined>} icon
   * @returns {string | undefined} The icon for the current state.
   */
  private getDisplayIcon(): string | undefined {
    if (!this.icon() && this.selectedTone() === 'favorite') {
      return this.selected() ? 'star-filled' : 'star'
    }

    return this.icon()
  }

  /**
   * Maps existing redesigned callers to the shared variant names.
   * @withSignal {InputSignal<ActionButtonVariant | undefined>} variant
   * @withSignal {InputSignal<string>} style
   * @withSignal {InputSignal<string>} color
   * @returns {ActionButtonVariant | undefined} The shared variant, or undefined for legacy styles.
   */
  private getDesignVariant(): ActionButtonVariant | undefined {
    if (this.variant()) {
      return this.variant()
    }

    if (this.style() === 'filled') {
      return 'primary'
    }

    if (this.style() === 'outlined') {
      return this.color() === 'red' ? 'destructive' : 'tertiary'
    }

    return undefined
  }
}
